import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Accordion, Alert, Badge, Button, Modal, Spinner, Table } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import rest from "../../../common/rest";

const prefix = "templateList.synchronize.preview.";
const entityOrder = ["Survey", "QuestionGroup", "Question", "Answer", "Indicator"];

export default function SynchronizationPreviewDialog({ template, onClose }) {
  const intl = useIntl();
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const { identifier, version } = template;
  const message = (key, values) => intl.formatMessage({ id: prefix + key }, values);
  const label = (key, fallback) => intl.formatMessage({ id: prefix + key, defaultMessage: fallback });

  useEffect(() => {
    let active = true;
    setResult(null);
    setError(null);
    const load = async () => {
      try {
        const response = await rest.doPost(
          `/admin/publish/survey/synchronize/${encodeURIComponent(identifier)}/version/${encodeURIComponent(version)}/preview`
        );
        if (!response || typeof response.surveyFound !== "boolean" || !Array.isArray(response.changes)) {
          throw new Error("Invalid preview response");
        }
        if (active) setResult(response);
      } catch (reason) {
        if (active) setError(Number(reason?.status) === 409 ? "duplicateKeys"
          : Number(reason?.status) === 400 ? "inactive" : "error");
      }
    };
    load();
    return () => { active = false; };
  }, [identifier, version, attempt]);

  const value = (field, current) => {
    if (current == null) return message("unset");
    if (current === "") return message("empty");
    if (current === "<generated>") return message("generated");
    if (typeof current === "boolean") return message(current ? "yes" : "no");
    if (field === "reportTemplateYear") {
      const date = new Date(current);
      if (!Number.isNaN(date.getTime())) return intl.formatDate(date, { year: "numeric", month: "long", timeZone: "UTC" });
    }
    return typeof current === "object" ? JSON.stringify(current) : String(current);
  };

  const groups = (result?.changes || []).reduce((all, change) => {
    (all[change.entity] = all[change.entity] || []).push(change);
    return all;
  }, {});
  const entities = Object.keys(groups).sort((a, b) => {
    const rank = entity => entityOrder.includes(entity) ? entityOrder.indexOf(entity) : entityOrder.length;
    return rank(a) - rank(b) || a.localeCompare(b);
  });
  const validationErrors = result?.validationErrors || [];
  const locationLabel = location => {
    const page = message("location.page", { index: location.pageIndex }) + (location.pageName ? `: ${location.pageName}` : "");
    return location.questionIndex == null ? page : `${page} · ${message("location.question", { index: location.questionIndex })}`
      + (location.questionName ? `: ${location.questionName}` : "");
  };

  return <Modal show size="xl" scrollable onHide={onClose} aria-labelledby="synchronization-preview-title">
    <Modal.Header closeButton>
      <Modal.Title id="synchronization-preview-title"><FormattedMessage id={prefix + "title"} /></Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <p className="fw-bold">{template.surveyName} · <FormattedMessage id="templateList.version" /> {version}</p>
      <p className="text-muted"><FormattedMessage id={prefix + "description"} /></p>
      {!result && !error && <div role="status" className="py-4 text-center">
        <Spinner animation="border" size="sm" aria-hidden="true" className="me-2" />{message("loading")}
      </div>}
      {error && <Alert variant="danger">{message(error)}</Alert>}
      {result && <>
        {validationErrors.length > 0 && <Alert variant="danger">
          <Alert.Heading>{message("blocked")}</Alert.Heading>
          <p>{message("duplicateKeys")}</p>
          <Table bordered responsive size="sm" className="mb-0">
            <thead><tr><th scope="col">{message("entity")}</th><th scope="col">{message("duplicateId")}</th><th scope="col">{message("locations")}</th></tr></thead>
            <tbody>{validationErrors.map(conflict => <tr key={`${conflict.entity}:${conflict.key}`}>
              <th scope="row">{label(`entity.${conflict.entity}`, conflict.entity)}</th>
              <td style={{ overflowWrap: "anywhere" }}><code>{conflict.key}</code></td>
              <td><ul className="mb-0">{conflict.locations.map((location, index) =>
                <li key={index}>{locationLabel(location)}</li>)}</ul></td>
            </tr>)}</tbody>
          </Table>
        </Alert>}
        {validationErrors.length === 0 && <>
        {!result.surveyFound && <Alert variant="warning">{message("notPublished")}</Alert>}
        {(result.warnings || []).filter(warning => !warning.startsWith("Survey not published;"))
          .map((warning, index) => <Alert variant="warning" key={index}>
            {warning === "Multiple surveys match; synchronization uses the first match." ? message("multipleSurveys") : warning}
          </Alert>)}
        {result.surveyFound && result.changes.length === 0 && <Alert variant="success">{message("noChanges")}</Alert>}
        {result.changes.length > 0 && <>
          <h5>{message("summary", { count: result.changes.length })}</h5>
          <Table bordered size="sm" responsive>
            <thead><tr><th scope="col">{message("entity")}</th><th scope="col">{message("CREATE")}</th><th scope="col">{message("UPDATE")}</th></tr></thead>
            <tbody>{entities.map(entity => <tr key={entity}>
              <th scope="row">{label(`entity.${entity}`, entity)}</th>
              <td>{groups[entity].filter(change => change.action === "CREATE").length}</td>
              <td>{groups[entity].filter(change => change.action === "UPDATE").length}</td>
            </tr>)}</tbody>
          </Table>
          <h5>{message("details")}</h5>
          <Accordion alwaysOpen>
            {entities.map(entity => <Accordion.Item eventKey={entity} key={entity}>
              <Accordion.Header>{label(`entity.${entity}`, entity)} <Badge bg="secondary" className="ms-2">{groups[entity].length}</Badge></Accordion.Header>
              <Accordion.Body>
                {groups[entity].map((change, index) => <details className="border rounded p-2 mb-2" key={`${change.gid ?? "new"}-${index}`}>
                  <summary style={{ overflowWrap: "anywhere" }}>
                    <Badge bg={change.action === "CREATE" ? "success" : "primary"} className="me-2">{label(change.action, change.action)}</Badge>
                    {change.after?.title || change.after?.text || change.key || label(`entity.${entity}`, entity)}
                    {change.gid != null && <span className="text-muted ms-2">ID {change.gid}</span>}
                  </summary>
                  {change.key && <p className="small text-muted mt-2" style={{ overflowWrap: "anywhere" }}>{change.key}</p>}
                  <Table bordered responsive size="sm" className="mt-2 mb-0" style={{ tableLayout: "fixed" }}>
                    <caption className="visually-hidden">{message("comparison")}</caption>
                    <thead><tr><th scope="col">{message("field")}</th><th scope="col">{message("before")}</th><th scope="col">{message("after")}</th></tr></thead>
                    <tbody>{Object.entries(change.after || {}).map(([field, current]) => <tr key={field}>
                      <th scope="row" style={{ overflowWrap: "anywhere" }}>{label(`field.${field}`, field)}</th>
                      <td style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{change.action === "CREATE" ? "—" : value(field, change.before?.[field])}</td>
                      <td style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{value(field, current)}</td>
                    </tr>)}</tbody>
                  </Table>
                </details>)}
              </Accordion.Body>
            </Accordion.Item>)}
          </Accordion>
        </>}
        </>}
      </>}
    </Modal.Body>
    <Modal.Footer>
      {error && <Button onClick={() => setAttempt(previous => previous + 1)}>{message("retry")}</Button>}
      <Button variant="secondary" onClick={onClose}><FormattedMessage id="app.common.Close" /></Button>
    </Modal.Footer>
  </Modal>;
}

SynchronizationPreviewDialog.propTypes = {
  template: PropTypes.shape({ identifier: PropTypes.string.isRequired, version: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    surveyName: PropTypes.string }).isRequired,
  onClose: PropTypes.func.isRequired
};
